'use client';

import { ExternalLink } from 'lucide-react';
import { useCallback, useMemo, type CSSProperties } from 'react';
import { CopyButtonBase, useCopyFeedback } from '@/components/ui/copy-button';
import { parseTextIntoSegments, type ListItem, type MarkerType } from './LegalTextFormatter';
import { cn } from '@/lib/utils';

/** Invoked when a citation header inside legal text is clicked. */
export type CitationClickHandler = (
  regulationIdOrCaseId: string,
  articleNumber?: string,
  verseNumber?: string,
  citationType?: 'regulation' | 'court_case',
) => void;

/** Renders the marker prefix of a list item based on its numbering scheme. */
function getMarkerDisplay(marker: string, markerType: MarkerType): string {
  switch (markerType) {
    case 'numbered':
      return `${marker}.`;
    case 'lettered':
      return `${marker}.`;
    case 'letter-paren':
      return `${marker})`;
    case 'paren-number':
      return `(${marker})`;
    case 'paren-letter':
      return `(${marker})`;
    case 'paren-alphanum':
      return `(${marker})`;
    case 'bullet':
      return '•';
    default:
      return marker;
  }
}

/** Computes the left indent for a nested list item. */
function getIndentStyle(level: number): CSSProperties {
  const indentRem = level === 0 ? 0 : level * 1.5 + 1;
  return { marginLeft: `${indentRem}rem` };
}

interface LegalTextMetadata {
  regulationTitle?: string;
  regulationId?: string;
  location?: string;
}

/**
 * Splits a legal text block into YAML front-matter metadata and the body.
 * Returns the original content untouched when no metadata header is present.
 */
function parseLegalTextMetadata(content: string): { metadata: LegalTextMetadata; textContent: string } {
  const metadata: LegalTextMetadata = {};
  let textContent = content;

  if (!content.startsWith('---\n')) {
    return { metadata, textContent };
  }

  const endIndex = content.indexOf('\n---\n', 4);
  if (endIndex === -1) {
    return { metadata, textContent };
  }

  const metadataBlock = content.slice(4, endIndex);
  textContent = content.slice(endIndex + 5).trim();

  const lines = metadataBlock.split('\n');
  for (const line of lines) {
    const colonIndex = line.indexOf(':');
    if (colonIndex === -1) continue;

    const key = line.slice(0, colonIndex).trim();
    const value = line.slice(colonIndex + 1).trim();

    switch (key) {
      case 'regulationTitle':
        metadata.regulationTitle = value;
        break;
      case 'regulationId':
        metadata.regulationId = value;
        break;
      case 'location':
        metadata.location = value;
        break;
      default:
        // Unknown front-matter keys are ignored; they do not affect rendering.
        break;
    }
  }

  return { metadata, textContent };
}

/** Extracts article and verse numbers from a citation location string. */
function extractArticleFromLocation(location: string): { articleNumber?: string; verseNumber?: string } {
  const articleMatch = location.match(/Pasal\s+(\d+[a-zA-Z]?)/i);
  const verseMatch = location.match(/ayat\s*\(?(\d+)\)?/i);

  return {
    articleNumber: articleMatch?.[1],
    verseNumber: verseMatch?.[1],
  };
}

interface LegalTextBlockProps {
  content: string;
  onCitationClick?: CitationClickHandler;
}

/**
 * Renders a fenced `legaltext` code block: an optional citation header, a
 * copyable body, and structured paragraphs/lists parsed from the content.
 */
export function LegalTextBlock({ content, onCitationClick }: LegalTextBlockProps) {
  const { metadata, textContent } = useMemo(() => parseLegalTextMetadata(content), [content]);
  const segments = useMemo(() => parseTextIntoSegments(textContent), [textContent]);
  const { copied, isCopying, setIsCopying, markCopied } = useCopyFeedback();

  const handleCopy = useCallback(async () => {
    setIsCopying(true);
    try {
      await navigator.clipboard.writeText(textContent);
      markCopied();
    } catch (error) {
      console.error('Failed to copy legal text:', error);
    } finally {
      setIsCopying(false);
    }
  }, [textContent, markCopied, setIsCopying]);

  const handleHeaderClick = useCallback(() => {
    if (metadata.regulationId && onCitationClick) {
      const { articleNumber, verseNumber } = metadata.location
        ? extractArticleFromLocation(metadata.location)
        : {};
      onCitationClick(metadata.regulationId, articleNumber, verseNumber);
    }
  }, [metadata.regulationId, metadata.location, onCitationClick]);

  const hasClickableHeader = metadata.regulationId && onCitationClick;

  return (
    <div className="my-4" data-blockquote-wrapper="true">
      {(metadata.regulationTitle || metadata.location) && (
        <button
          data-blockquote-subtitle="true"
          onClick={hasClickableHeader ? handleHeaderClick : undefined}
          className={cn(
            'flex items-center gap-1 mb-1 text-left text-[10px] text-neutral-400',
            hasClickableHeader && 'hover:text-blue-600 cursor-pointer',
          )}
          disabled={!hasClickableHeader}
        >
          {[metadata.location, metadata.regulationTitle].filter(Boolean).join(' • ')}
          {hasClickableHeader && <ExternalLink size={10} />}
        </button>
      )}

      <div className="group relative bg-neutral-50 border border-neutral-200 rounded-lg p-4">
        <CopyButtonBase
          onCopy={handleCopy}
          isCopying={isCopying}
          copied={copied}
          variant="floating"
          className="absolute top-2 right-2"
        />

        <div className="leading-relaxed tracking-tight" data-blockquote-body="true">
          {segments.map((segment, idx) => {
            if (segment.type === 'list' && segment.listItems) {
              return <LegalListRenderer key={segment.id || idx} items={segment.listItems} />;
            }

            if (segment.content.trim()) {
              return (
                <div
                  key={segment.id || idx}
                  className="mb-3 last:mb-0 whitespace-pre-wrap text-neutral-900 leading-[1.7] text-justify"
                >
                  {segment.content}
                </div>
              );
            }

            return null;
          })}
        </div>
      </div>
    </div>
  );
}

/** Renders the structured list items of a legal text segment. */
function LegalListRenderer({ items }: { items: ListItem[] }) {
  return (
    <div className="space-y-1 mb-3">
      {items.map((item, idx) => {
        const markerDisplay = getMarkerDisplay(item.marker, item.markerType);
        const indentStyle = getIndentStyle(item.level);

        return (
          <div key={idx} className="flex gap-2" style={indentStyle}>
            <span className="font-medium text-gray-700 flex-shrink-0 select-none min-w-[1.5rem]">
              {markerDisplay}
            </span>
            <div className="flex-1 text-justify leading-[1.7] text-neutral-900">
              {item.content}
            </div>
          </div>
        );
      })}
    </div>
  );
}
